import { FlightExchangeTripDetailsCard } from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useFlightExchangeRebook } from "@tenant/flights";
import { Button, ButtonTypeEnum, Page, PriceBreakdown } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FlightExchangeRebook.module.scss";

export type FlightExchangeRebookProps = PlatformComponentProps;

export const FlightExchangeRebook = ({
  className,
}: FlightExchangeRebookProps) => {
  const { copy, context, handlers } = useFlightExchangeRebook();
  const COMPONENT_KEY = CoreFlightsComponent.FlightExchangeRebook;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  // Rebook Title components
  // Rebook title
  const title = (
    <div>
      <span className={cn("title-text")}>{copy.rebookTitle}</span>
    </div>
  );
  // Rebook subtitle
  const subtitle = (
    <div>
      <span className={cn("subtitle-text")}>{copy.rebookSubtitle}</span>
    </div>
  );

  // Trip details
  const tripDetails = <FlightExchangeTripDetailsCard />;

  // Contact details
  const contactDetailsHeader = (
    <div className={cn("section-header")}>
      <div className={cn("title")}>{copy.contactInfo}</div>
    </div>
  );
  const contactDetails = (
    <ol className={cn("contact-details")}>
      <li className={cn("data-header-with-label")}>
        <div className={cn("data-header-with-label-header")}>
          {copy.phoneNumber}
        </div>
        <div className={cn("data-header-with-label-data")}>
          {context.contactInfo.phone}
        </div>
      </li>
      <li className={cn("data-header-with-label")}>
        <div className={cn("data-header-with-label-header")}>{copy.email}</div>
        <div className={cn("data-header-with-label-data")}>
          {context.contactInfo.email}
        </div>
      </li>
    </ol>
  );

  // Traveler information
  const travelersHeader = (
    <div className={cn("section-header")}>
      <div className={cn("title")}>{copy.travelerInfo}</div>
    </div>
  );
  const travelers = (
    <ol className={cn("contact-details")}>
      {context.travelers?.alone.map((a) => {
        return (
          <li className={cn("traveler-info-name")}>
            {a.person.givenName + " " + a.person.surname}
            {/*TODO: change this type*/}
            <div className={cn("traveler-info-type")}>{a.type}</div>
          </li>
        );
      })}
    </ol>
  );

  // Price breakdown
  const priceBreakdown = (
    <PriceBreakdown
      className={cn("price-breakdown")}
      isLoading={false}
      priceLineItems={context.priceBreakdown.tripPriceLineItems}
      summaryLineItems={context.priceBreakdown.summaryLineItems}
      title={copy.priceBreakdown}
      currencyString="USD"
    />
  );
  const paymentMethod = (
    <div>
      <div className={cn("section-header")}>{copy.paymentMethod}</div>
      <div className={cn("payment-method-title")}>
        {context.paymentMethod.identifier}
      </div>
      <div className={cn("payment-method-type")}>
        {context.paymentMethod.type}
      </div>
    </div>
  );
  // Payable difference
  const payableDifference = (
    <div>
      <div className={cn("payable-difference")}>
        {context.totalPayableDifference}
      </div>
      <div className={cn("payable-difference-subtitle")}>
        {copy.totalPayableDifference}
      </div>
    </div>
  );
  // Rebook button
  const rebookButton = (
    <Button
      buttonType={ButtonTypeEnum.Primary}
      className={cn("confirm-button")}
      onClick={handlers.scheduleFulfillRebook}
    >
      {copy.confirmButton}
    </Button>
  );

  return (
    <div className={clsx(block, className)}>
      <Page title={copy.rebookTitle} className={cn("rebook-page")}>
        <div className={cn("rebook-page-content")}>
          <div>
            {title}
            {subtitle}
          </div>
          <div>{tripDetails}</div>
          <div>
            {contactDetailsHeader}
            {contactDetails}
          </div>
          <div>
            {travelersHeader}
            {travelers}
          </div>
          {priceBreakdown}
          {paymentMethod}
          <hr className={cn("solid-separator")} />
          {payableDifference}
          {rebookButton}
        </div>
      </Page>
    </div>
  );
};
